import { useMemo, useState } from "react";
import { Calculator, ArrowUpRight, Info } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/contexts/LanguageContext";

const fmt = (n) =>
  n.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 });

/** Small projection tool: capital x monthly target, compounded over N months. */
const ProfitEstimator = () => {
  const { t } = useLanguage();
  const [capital, setCapital] = useState(1000);
  const [rate, setRate] = useState(3);
  const [months, setMonths] = useState(12);

  const { final, profit, simple, series } = useMemo(() => {
    const r = rate / 100;
    const finalValue = capital * Math.pow(1 + r, months);
    const points = Array.from({ length: months + 1 }, (_, m) => ({
      month: m,
      compound: Math.round(capital * Math.pow(1 + r, m)),
      flat: Math.round(capital + capital * r * m),
    }));
    return {
      final: finalValue,
      profit: finalValue - capital,
      simple: capital * r * months,
      series: points,
    };
  }, [capital, rate, months]);

  return (
    <section
      aria-labelledby="estimator-heading"
      data-testid="profit-estimator"
      className="mt-14 md:mt-20 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-10"
    >
      <header className="flex items-start gap-3">
        <span
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[rgb(var(--brand-rgb)/0.12)] text-[var(--brand)]"
          aria-hidden="true"
        >
          <Calculator className="h-5 w-5" />
        </span>
        <div>
          <h3
            id="estimator-heading"
            className="font-heading text-xl md:text-2xl font-bold tracking-tight text-white"
          >
            {t.estimator.title}
          </h3>
          <p className="mt-1 text-sm md:text-base text-zinc-200">
            {t.estimator.subtitle}
          </p>
        </div>
      </header>

      <div className="mt-8 grid lg:grid-cols-5 gap-8 lg:gap-10">
        <div className="lg:col-span-3 space-y-7">
          <div>
            <label
              htmlFor="estimator-capital"
              className="flex items-baseline justify-between text-sm font-semibold text-zinc-200"
            >
              {t.estimator.capital}
              <span className="font-heading text-base text-white">
                {fmt(capital)} USDT
              </span>
            </label>
            <input
              id="estimator-capital"
              type="number"
              min="100"
              step="50"
              value={capital}
              onChange={(e) => setCapital(Math.max(0, Number(e.target.value) || 0))}
              data-testid="estimator-capital-input"
              className="mt-2 w-full h-12 rounded-xl border border-white/15 bg-black/40 px-4 text-white font-mono focus:border-[var(--brand)] outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="estimator-rate"
              className="flex items-baseline justify-between text-sm font-semibold text-zinc-200"
            >
              {t.estimator.rate}
              <span className="font-heading text-base text-white">{rate}%</span>
            </label>
            <Slider
              id="estimator-rate"
              value={[rate]}
              onValueChange={([v]) => setRate(v)}
              min={1}
              max={10}
              step={0.5}
              className="mt-4"
              data-testid="estimator-rate-slider"
              aria-label={t.estimator.rate}
            />
          </div>

          <div>
            <span className="block text-sm font-semibold text-zinc-200">
              {t.estimator.months}
            </span>
            <div
              role="group"
              aria-label={t.estimator.months}
              className="mt-3 flex flex-wrap gap-2"
            >
              {[3, 6, 12, 24].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMonths(m)}
                  aria-pressed={months === m}
                  data-testid={`estimator-months-${m}`}
                  className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
                    months === m
                      ? "bg-[var(--brand)] text-black"
                      : "border border-white/15 text-white hover:border-[var(--brand)]"
                  }`}
                >
                  {m} {t.estimator.month_unit}
                </button>
              ))}
            </div>
          </div>
        </div>

        <dl className="lg:col-span-2 rounded-2xl border border-[rgb(var(--brand-rgb)/0.3)] bg-[rgb(var(--brand-rgb)/0.05)] p-6 flex flex-col justify-center">
          <dt className="text-[10px] uppercase tracking-[0.2em] text-zinc-200">
            {t.estimator.result_final}
          </dt>
          <dd
            data-testid="estimator-final"
            className="ml-0 font-heading text-4xl md:text-5xl font-black tracking-tighter text-[var(--brand)] glow-text-soft"
          >
            {fmt(final)}
            <span className="text-base font-semibold text-zinc-200 ml-2">USDT</span>
          </dd>

          <dt className="mt-5 text-[10px] uppercase tracking-[0.2em] text-zinc-200">
            {t.estimator.result_profit}
          </dt>
          <dd
            data-testid="estimator-profit"
            className="ml-0 font-heading text-2xl font-bold text-white"
          >
            +{fmt(profit)} USDT
          </dd>

          <dt className="mt-5 text-[10px] uppercase tracking-[0.2em] text-zinc-200">
            {t.estimator.result_simple}
          </dt>
          <dd data-testid="estimator-simple" className="ml-0 text-base text-zinc-200">
            +{fmt(simple)} USDT
          </dd>
        </dl>
      </div>

      <figure className="mt-8" data-testid="estimator-chart">
        <figcaption className="text-xs uppercase tracking-[0.2em] text-zinc-300 font-semibold mb-4">
          {t.estimator.chart_title}
        </figcaption>
        <div className="h-56 md:h-72 w-full" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="estFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: "#a1a1aa", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.12)" }}
                tickFormatter={(m) => `${m}`}
              />
              <YAxis
                tick={{ fill: "#a1a1aa", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
              />
              <Tooltip
                contentStyle={{
                  background: "#0A0A0A",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                labelFormatter={(m) => `${t.estimator.month_label} ${m}`}
                formatter={(value, key) => [
                  `${fmt(value)} USDT`,
                  key === "compound"
                    ? t.estimator.legend_compound
                    : t.estimator.legend_flat,
                ]}
              />
              <Area
                type="monotone"
                dataKey="flat"
                stroke="rgba(255,255,255,0.35)"
                strokeDasharray="4 4"
                fill="none"
                strokeWidth={1.5}
              />
              <Area
                type="monotone"
                dataKey="compound"
                stroke="var(--brand)"
                strokeWidth={2.5}
                fill="url(#estFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <table className="sr-only" data-testid="estimator-chart-table">
          <caption>{t.estimator.chart_title}</caption>
          <thead>
            <tr>
              <th scope="col">{t.estimator.month_label}</th>
              <th scope="col">{t.estimator.legend_compound}</th>
              <th scope="col">{t.estimator.legend_flat}</th>
            </tr>
          </thead>
          <tbody>
            {series.map((row) => (
              <tr key={row.month}>
                <th scope="row">{row.month}</th>
                <td>{fmt(row.compound)} USDT</td>
                <td>{fmt(row.flat)} USDT</td>
              </tr>
            ))}
          </tbody>
        </table>
      </figure>

      <p className="mt-7 flex items-start gap-2 text-xs md:text-sm text-zinc-300 leading-relaxed">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-[var(--accent2)]" aria-hidden="true" />
        {t.estimator.disclaimer}
      </p>

      <a
        href="https://aiotrade.id/compound.html"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="estimator-compound-link"
        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--brand)] hover:underline"
      >
        {t.estimator.compound_link}
        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only"> {t.a11y.external}</span>
      </a>
    </section>
  );
};

export default ProfitEstimator;
